import { useEffect, useState } from "react";
import { useSeoMeta } from "@unhead/react";
import { Link } from "react-router-dom";
import { ArrowLeft, GraduationCap, Lock, Printer, Award } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Confetti } from "@/components/Confetti";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShareCertificate } from "@/components/ShareCertificate";
import { DonateCard } from "@/components/DonateCard";
import { NextSteps } from "@/components/NextSteps";
import { useProgress } from "@/hooks/useProgress";
import { TOTAL_LESSONS } from "@/lib/curriculum";

export default function CertificatePage() {
  const {
    allComplete,
    allLessonsComplete,
    quizPassed,
    xp,
    totalXp,
    earnedBadges,
    completedCount,
    state,
    setLearnerName,
  } = useProgress();

  useSeoMeta({
    title: "Certificate — BCH School",
    description: "Your BCH School completion certificate.",
  });

  // Fire a celebratory confetti burst when the completed certificate first
  // appears. Only runs once per page mount, and only when actually earned.
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => {
    if (allComplete) {
      const t = setTimeout(() => setCelebrate(true), 250);
      return () => clearTimeout(t);
    }
  }, [allComplete]);

  const name = state.learnerName.trim() || "BCH Scholar";
  const dateStr = state.completedAt
    ? new Date(state.completedAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

  if (!allComplete) {
    // Two lock states: still finishing lessons, or lessons done but quiz not passed.
    const needsQuizOnly = allLessonsComplete && !quizPassed;
    return (
      <Layout>
        <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
          <Link to="/">
            <ArrowLeft className="mr-1 h-4 w-4" /> Home
          </Link>
        </Button>
        <Card className="border-2 border-dashed">
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary">
              <Lock className="h-7 w-7 text-muted-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Certificate locked</h1>
              {needsQuizOnly ? (
                <p className="mx-auto mt-2 max-w-sm text-muted-foreground">
                  You've finished every lesson — nice work! One last step: pass
                  the final quiz to unlock your official BCH School certificate.
                </p>
              ) : (
                <p className="mx-auto mt-2 max-w-sm text-muted-foreground">
                  Complete all {TOTAL_LESSONS} lessons and pass the final quiz to
                  unlock your official BCH School certificate. You're at{" "}
                  {completedCount}/{TOTAL_LESSONS} lessons!
                </p>
              )}
            </div>
            <Button asChild size="lg">
              <Link to={needsQuizOnly ? "/quiz" : "/"}>
                {needsQuizOnly ? "Take the final quiz 🧠" : "Keep learning"}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout>
      <Confetti active={celebrate} />
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2 print:hidden">
        <Link to="/">
          <ArrowLeft className="mr-1 h-4 w-4" /> Home
        </Link>
      </Button>

      {/* Name input */}
      <Card className="mb-6 border-2 print:hidden">
        <CardContent className="flex flex-col gap-3 p-6 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label htmlFor="name">Name on certificate</Label>
            <Input
              id="name"
              className="mt-1"
              placeholder="Enter your name or handle"
              value={state.learnerName}
              onChange={(e) => setLearnerName(e.target.value)}
              maxLength={40}
            />
          </div>
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" /> Print / Save PDF
          </Button>
        </CardContent>
      </Card>

      {/* The certificate */}
      <div className="mx-auto max-w-2xl">
        <div className="relative overflow-hidden rounded-3xl border-4 border-primary/30 bg-gradient-to-br from-white to-green-50 p-8 text-center shadow-lg dark:from-card dark:to-secondary sm:p-12 print:shadow-none">
          {/* corner flourishes */}
          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/10" />
          <div className="pointer-events-none absolute -bottom-12 -left-12 h-48 w-48 rounded-full bg-emerald-500/10" />

          <div className="relative">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-md">
              <GraduationCap className="h-8 w-8" />
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
              Certificate of Completion
            </p>
            <h1 className="mt-4 text-2xl font-semibold text-muted-foreground">
              This certifies that
            </h1>
            <p className="mt-2 break-words bg-gradient-to-r from-green-600 to-emerald-700 bg-clip-text text-4xl font-extrabold text-transparent">
              {name}
            </p>
            <p className="mx-auto mt-4 max-w-md text-muted-foreground">
              has successfully completed the{" "}
              <strong className="text-foreground">BCH School</strong>{" "}
              curriculum, mastering the fundamentals of using Bitcoin Cash as
              everyday digital cash.
            </p>

            <div className="mt-8 flex items-center justify-center gap-8 text-sm">
              <div>
                <div className="text-2xl font-extrabold text-primary">{xp}</div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  / {totalXp} XP
                </div>
              </div>
              <div className="h-10 w-px bg-border" />
              <div>
                <div className="text-2xl font-extrabold text-primary">
                  {earnedBadges.length}
                </div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Badges
                </div>
              </div>
              <div className="h-10 w-px bg-border" />
              <div>
                <div className="flex items-center justify-center">
                  <Award className="h-6 w-6 text-primary" />
                </div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Graduate
                </div>
              </div>
            </div>

            <div className="mt-8 border-t pt-4 text-xs text-muted-foreground">
              Awarded {dateStr} · BCH School · Learn Bitcoin Cash by doing
            </div>
          </div>
        </div>
      </div>

      {/* Share */}
      <Card className="mx-auto mt-6 max-w-2xl border-2 print:hidden">
        <CardContent className="space-y-4 p-6">
          <div>
            <h2 className="font-bold">Share your achievement</h2>
            <p className="text-sm text-muted-foreground">
              Download your certificate as an image, or post it to X and Nostr.
            </p>
          </div>
          <ShareCertificate
            data={{
              name,
              xp,
              totalXp,
              badgeCount: earnedBadges.length,
              dateStr,
            }}
            shareText={`I just graduated from BCH School and earned my certificate! 🎓 I learned how to use Bitcoin Cash as everyday digital cash — ${xp} XP and ${earnedBadges.length} badges. Learn by doing at BCH School! ⚡`}
          />
        </CardContent>
      </Card>

      {/* Optional tip — the "found this valuable?" moment */}
      <DonateCard />

      {/* Real-world next steps */}
      <div className="mx-auto mt-10 max-w-2xl print:hidden">
        <NextSteps />
      </div>
    </Layout>
  );
}
